import React, { FC, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Redirect, Route, Switch, useHistory } from 'react-router-dom'
import uniqBy from 'lodash/uniqBy'
import { Toast, TToastNewState, useToastState } from 'transfix-ui/components/Toast'
import { Box } from 'transfix-ui/core'
import { TeamMemberManageModal } from 'apps/fleet-planner/components/team/TeamMemberManageModal'
import { useTeamMemberContext } from 'apps/fleet-planner/context/TeamMemberContext'
import { ITeamMember } from 'apps/fleet-planner/types'
import { fetchDispatchers } from 'apps/fleet-planner/utils/actions/dispatchers'
import { fetchDrivers } from 'apps/fleet-planner/utils/actions/drivers'
import { hasDispatchers, hasDrivers } from 'utils/teamMemberHelpers'
import { TeamMemberInfoPage } from './TeamMemberInfoPage'
import { TeamMembersPage } from './TeamMembersPage'
import { MODAL_MODES } from './constants'

export const TeamRouterPage: FC = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const {
    modalMode,
    selectedTeamMember,
    setModalMode,
    setSelectedTeamMember,
    setTeamMembers,
    teamMembers,
  } = useTeamMemberContext()
  const [lastUpdatedTeamMemberId, setLastUpdatedTeamMemberId] = useState<number | undefined>(
    undefined
  )
  const { closeToast, multiLine, openToast, toastMessage, toastOpen, toastType } = useToastState()

  const shouldRedirectToAddPage = !selectedTeamMember && modalMode === MODAL_MODES.ADD_NEW
  const shouldRedirectToEditPage = selectedTeamMember && modalMode === MODAL_MODES.EDIT_EXISTING

  const teamMemberEditHandler = (teamMember?: ITeamMember) => {
    setSelectedTeamMember(teamMember)
    setModalMode(MODAL_MODES.EDIT_EXISTING)
  }

  const closeModalHandler = () => {
    if (modalMode === MODAL_MODES.ADD_NEW) {
      setSelectedTeamMember(undefined)
    }
    setModalMode(undefined)
    history.push('/team')
  }

  const pressBackButtonHandler = () => {
    setSelectedTeamMember(undefined)
    setModalMode(undefined)
    history.push('/team')
  }

  const updateTeamMemberSubmitHandler = (
    toastState: TToastNewState,
    updatedTeamMember?: ITeamMember
  ) => {
    openToast(toastState)
    if (updatedTeamMember) {
      setLastUpdatedTeamMemberId(updatedTeamMember?.id)

      // update team members list
      setTeamMembers(
        uniqBy([updatedTeamMember, ...teamMembers], 'id').filter(tm => {
          const { motorCarrierDetails = [] } = tm || {}
          return !!(motorCarrierDetails?.length || 0)
        })
      )

      // fetch updated redux user data
      if (hasDrivers(updatedTeamMember.motorCarrierDetails)) {
        dispatch(fetchDrivers())
      }

      if (hasDispatchers(updatedTeamMember.motorCarrierDetails)) {
        dispatch(fetchDispatchers())
      }
    }
  }

  return (
    <Box>
      {shouldRedirectToAddPage && <Redirect to='/team/member/add' push />}
      {shouldRedirectToEditPage && (
        <Redirect to={`/team/member/${selectedTeamMember?.id}/edit`} push />
      )}

      <Switch>
        {/* View one team member */}
        <Route path='/team/member/:teamMemberId/view' exact>
          <TeamMemberInfoPage
            onPressBackButton={pressBackButtonHandler}
            onEditTeamMember={teamMemberEditHandler}
          />
        </Route>

        {/* List all team members */}
        <Route path='/team'>
          <TeamMembersPage
            lastUpdatedTeamMemberId={lastUpdatedTeamMemberId}
            onTeamMemberEdit={teamMemberEditHandler}
          />
        </Route>
      </Switch>

      {/* Add a team member */}
      <Route path='/team/member/add' exact>
        <TeamMemberManageModal
          onClose={closeModalHandler}
          onSubmit={updateTeamMemberSubmitHandler}
        />
      </Route>

      {/* Edit a team member */}
      <Route path='/team/member/:teamMemberId/edit' exact>
        <TeamMemberManageModal
          onClose={closeModalHandler}
          onSubmit={updateTeamMemberSubmitHandler}
        />
      </Route>

      <Toast
        multiLine={multiLine}
        duration={multiLine ? 10000 : null}
        variation={toastType}
        open={toastOpen}
        onClose={closeToast}
        message={toastMessage}
      />
    </Box>
  )
}

export default TeamRouterPage
